import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  DirectoryUser,
  DIRECTORY_PAGE_SIZE,
  directoryPage,
} from './userDirectoryModel';
import { formatPersonName } from '../../utils/formatName';

const action =
  'rounded px-2 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600';
function accessDate(value: string | null) {
  if (!value) return 'Sin acceso previo';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Fecha no disponible'
    : date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}
export default function UsersDirectory({
  users,
  loading,
  error,
  retry,
  isAdmin,
  currentUserId,
  onEdit,
  onResend,
  onDelete,
}: {
  users: DirectoryUser[];
  loading: boolean;
  error: boolean;
  retry: () => void;
  isAdmin: boolean;
  currentUserId?: string;
  onEdit: (user: DirectoryUser) => void;
  onResend: (user: DirectoryUser) => void;
  onDelete: (user: DirectoryUser) => void;
}) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const page = directoryPage(params.get('page'), users.length);
  const start = (page - 1) * DIRECTORY_PAGE_SIZE;
  const visible = users.slice(start, start + DIRECTORY_PAGE_SIZE);
  const goPage = (nextPage: number) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('page', String(nextPage));
      return next;
    });
    setExpanded(null);
    document.getElementById('directory-results')?.focus();
  };
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <h2
          id="directory-results"
          tabIndex={-1}
          className="text-sm font-medium"
          aria-live="polite"
        >
          {loading
            ? 'Cargando usuarios…'
            : error
              ? 'Directorio no disponible'
              : `${users.length} ${users.length === 1 ? 'usuario' : 'usuarios'}`}
        </h2>
        <label className="text-sm text-slate-600">
          Ordenar por{' '}
          <select
            value={params.get('sort') === 'recent' ? 'recent' : 'name'}
            onChange={(event) => {
              const value = event.target.value;
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set('sort', value);
                next.delete('page');
                return next;
              });
            }}
            className="ml-2 rounded border border-slate-200 bg-white p-1.5"
          >
            <option value="name">Nombre</option>
            <option value="recent">Acceso reciente</option>
          </select>
        </label>
      </div>
      {loading ? (
        <div
          role="status"
          className="border-t border-slate-100 px-5 py-12 text-sm text-slate-500"
        >
          Cargando el directorio…
        </div>
      ) : error ? (
        <p role="alert" className="px-5 py-10 text-sm text-red-800">
          No se pudieron cargar los usuarios.{' '}
          <button onClick={retry} className="underline">
            Reintentar usuarios
          </button>
        </p>
      ) : users.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <p className="font-medium">No se encontraron usuarios</p>
          <p className="mt-2 text-sm text-slate-600">
            Prueba otro nombre, correo o combinación de filtros.
          </p>
        </div>
      ) : (
        <>
          <div
            aria-hidden="true"
            className="hidden grid-cols-12 gap-4 border-y border-slate-200 bg-slate-50 px-5 py-2 text-xs font-medium text-slate-500 md:grid"
          >
            <span className="col-span-4">Usuario</span>
            <span className="col-span-3">Organización</span>
            <span className="col-span-2">Cuenta</span>
            <span className="col-span-3 text-right">Acciones</span>
          </div>
          <ul
            aria-label="Usuarios encontrados"
            className="divide-y divide-slate-100"
          >
            {visible.map((user) => {
              const orgs = [...(user.user_organization || [])]
                .filter((item) => item.is_active)
                .sort((a, b) => Number(b.is_main) - Number(a.is_main));
              const name = formatPersonName(user.full_name) || user.email;
              const open = expanded === user.id;
              return (
                <li key={user.id} className="px-4 py-3 sm:px-5">
                  <div className="grid grid-cols-2 items-center gap-x-4 gap-y-2 md:grid-cols-12">
                    <div className="col-span-2 min-w-0 md:col-span-4">
                      <p className="break-words text-sm font-medium">{name}</p>
                      <p className="mt-0.5 break-all text-xs text-slate-500">
                        {user.email}
                      </p>
                    </div>
                    <div className="min-w-0 text-sm text-slate-600 md:col-span-3">
                      <span>
                        {orgs[0]?.organization.name || 'Sin organización'}
                      </span>
                      {orgs.length > 1 && (
                        <details className="mt-1 text-xs">
                          <summary className="cursor-pointer text-blue-700">
                            {orgs.length - 1} más
                          </summary>
                          <ul className="mt-1 space-y-1">
                            {orgs.slice(1).map((item) => (
                              <li key={item.organization.id}>
                                {item.organization.name}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                    <div className="text-sm md:col-span-2">
                      <span
                        className={
                          user.is_active ? 'text-slate-700' : 'text-red-800'
                        }
                      >
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {user.role === 'admin'
                          ? 'Administrador'
                          : user.role === 'reviewer'
                            ? 'Revisor'
                            : user.is_internal
                              ? 'Usuario interno'
                              : 'Usuario'}
                      </p>
                    </div>
                    <div className="col-span-2 flex flex-wrap items-center gap-2 md:col-span-3 md:justify-end">
                      {user.role === 'normal' && (
                        <Link
                          id={`user-${user.id}`}
                          className={action}
                          aria-label={`Ver documentos de ${name}`}
                          to={`/review/${user.id}?${new URLSearchParams({ returnTo: `/admin/users${location.search}#user-${user.id}` })}`}
                        >
                          Documentos
                        </Link>
                      )}
                      {isAdmin && (
                        <button
                          aria-expanded={open}
                          aria-controls={`account-${user.id}`}
                          aria-label={`Gestionar cuenta de ${name}`}
                          onClick={() => setExpanded(open ? null : user.id)}
                          className="rounded px-2 py-2 text-sm text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
                        >
                          Cuenta
                        </button>
                      )}
                    </div>
                  </div>
                  {isAdmin && open && (
                    <div
                      id={`account-${user.id}`}
                      className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3"
                    >
                      <p className="text-xs text-slate-500">
                        Último acceso: {accessDate(user.last_login)}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button className={action} onClick={() => onEdit(user)}>
                          Editar datos y acceso
                        </button>
                        <button
                          className={action}
                          onClick={() => onResend(user)}
                        >
                          Reenviar invitación
                        </button>
                        {user.id !== currentUserId && (
                          <button
                            className="rounded px-2 py-2 text-sm text-red-800 hover:bg-red-50"
                            onClick={() => onDelete(user)}
                          >
                            Eliminar usuario
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <nav
            aria-label="Páginas de usuarios"
            className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 text-sm"
          >
            <span className="text-slate-500">
              {start + 1}–{Math.min(start + DIRECTORY_PAGE_SIZE, users.length)}{' '}
              de {users.length}
            </span>
            <div className="flex items-center gap-3">
              <button
                disabled={page === 1}
                onClick={() => goPage(page - 1)}
                className="rounded px-2 py-2 text-blue-700 disabled:text-slate-400"
              >
                Anterior
              </button>
              <span>Página {page}</span>
              <button
                disabled={start + DIRECTORY_PAGE_SIZE >= users.length}
                onClick={() => goPage(page + 1)}
                className="rounded px-2 py-2 text-blue-700 disabled:text-slate-400"
              >
                Siguiente
              </button>
            </div>
          </nav>
        </>
      )}
    </>
  );
}
